import { Mutex } from 'async-mutex';

import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import env from '@config/env';

import { UserWithRole } from '@interfaces/auth.interface';

import { logout, setCredentials } from '@slices/auth.slice';
import i18n from '../i18n/i18n';

export function isApiError(
  error: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): error is FetchBaseQueryError & { data: any } {
  return typeof error === 'object' && error != null && 'status' in error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BASE_URL = window['API_BASE_URL' as any] as any;

const LOGIN_PATH = env.API_LOGIN_PATH;
const REFRESH_PATH = env.API_REFRESH_PATH;

// Create a new mutex
const mutex = new Mutex();

// Use this variable to prevent multiple refresh if the first one fails
let hasRefreshFailed = false;

const baseQueryFn = fetchBaseQuery({
  baseUrl: BASE_URL || 'http://localhost:5000/api',
  credentials: 'include',
  prepareHeaders: async (headers) => {
    headers.set('x-lang', i18n.language);
  },
});

const baseQuery: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // Reset refresh failed flag
  hasRefreshFailed = false;

  // Wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  let result = await baseQueryFn(args, api, extraOptions);

  if (
    !hasRefreshFailed &&
    args.url !== LOGIN_PATH &&
    args.url !== REFRESH_PATH &&
    result.error?.status === 401
  ) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Try refresh token
        const refreshResult = await baseQueryFn(
          { url: REFRESH_PATH, method: 'POST' },
          api,
          extraOptions,
        );

        if (refreshResult.error) {
          api.dispatch(logout());

          // Release mutex
          hasRefreshFailed = true;
          release();

          return result;
        } else {
          const user = refreshResult.data as UserWithRole;

          api.dispatch(setCredentials({ user }));

          // Retry the initial query
          result = await baseQueryFn(args, api, extraOptions);
        }
      } finally {
        // Release  the mutex
        release();
      }
    } else {
      // Wait until the mutex is available without locking it
      await mutex.waitForUnlock();

      // Don't retry if the refresh failed
      if (!hasRefreshFailed) {
        result = await baseQueryFn(args, api, extraOptions);
      }
    }
  }

  return result;
};

const api = createApi({
  baseQuery,
  tagTypes: ['holidays', 'coupons', 'whishlists', 'bookings', 'customers'], // Define here tags for cache invalidation
  endpoints: () => ({}),
});

export default api;
